import React, { useState, useEffect } from 'react';
import { RotateCcw, Trash2, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { employeeApi } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function RecycleBin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const r = await employeeApi.trashList(); setRows(r.data); }
    catch (e) { toast.error('Failed to load recycle bin'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => { try { await employeeApi.restore(id); toast.success('Restored'); await load(); } catch { toast.error('Restore failed'); } };
  const purge = async (r) => {
    if (!window.confirm(`Permanently delete ${r.name}? This removes their salary, KPI and document records and cannot be undone.`)) return;
    try { await employeeApi.remove(r.id); toast.success('Permanently deleted'); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="recycle-bin-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-2"><Archive className="w-6 h-6 text-[#5A6377]" /><h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554]">Recycle Bin</h1></div>
            <p className="text-sm text-[#5A6377] mb-6">{rows.length} deleted employees — restore them, or delete permanently.</p>
            {loading ? <div className="text-sm text-[#5A6377]">Loading…</div> : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#102468] text-white text-xs font-semibold uppercase tracking-wide">
                    <tr><th className="py-3 px-4 text-left">Name</th><th className="py-3 px-4 text-left">Card</th><th className="py-3 px-4 text-left">Unit</th><th className="py-3 px-4 text-left">Department</th><th className="py-3 px-4 text-left">Designation</th><th className="py-3 px-4 text-left">Deleted</th><th className="py-3 px-4 text-left">Actions</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} data-testid={`trash-row-${r.id}`} className="border-b border-[#E6E9F0] last:border-0 hover:bg-[#F7F8FB]">
                        <td className="py-3 px-4 text-sm font-medium text-[#11182B]">{r.name}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.card_no}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.unit}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.department}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.designation}</td>
                        <td className="py-3 px-4 text-xs text-[#98A1B2]">{r.deleted_at ? new Date(r.deleted_at).toLocaleDateString() : '—'}</td>
                        <td className="py-3 px-4 flex gap-2">
                          <Button data-testid={`restore-${r.id}`} variant="outline" size="sm" className="text-[#15A36E] border-[#BDE9D3]" onClick={() => restore(r.id)}><RotateCcw className="w-4 h-4 mr-1" />Restore</Button>
                          <Button data-testid={`purge-${r.id}`} variant="outline" size="sm" className="text-[#E11D2A] border-[#F5C9CC]" onClick={() => purge(r)}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-[#98A1B2]">Recycle bin is empty</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
